import { Panel } from "@xyflow/react";
import { Suspense, useCallback, useRef } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Button, Text } from "@zapier/design-system";
import {
  selectedNodesAtom,
  deselectAllEdgesSetter,
  deselectAllNodesSetter,
  autoLayoutSelectedNodesSetter,
  hasExpandedSelectedZapNodesAtom,
  actorsAtomFamily,
  loadableUniqueAppsForSelectedNodesAtom,
  createNotificationSetter,
  saveCanvasSetter,
  graphDirectionAtom,
} from "@zapier/canvas-state";

import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";
import { CloseButton } from "../../components/CloseButton";
import { SummaryActors } from "../SummaryActors";
import { SummaryApps } from "../SummaryApps";
import { AutoLayoutIcon } from "../icons/AutoLayoutIcon";
import { AutoLayoutSuccessMessage } from "../AutoLayoutSuccessMessage";

import styles from "./MultiSelectPanel.module.css";

function SuspendedSummaryAppsWrapper() {
  const loadableApps = useAtomValue(loadableUniqueAppsForSelectedNodesAtom);
  const previousAppsRef = useRef<string[]>([]);

  if (loadableApps.state === "hasData") {
    previousAppsRef.current = loadableApps.data;
  }

  const uniqueApps =
    loadableApps.state === "hasData"
      ? loadableApps.data
      : previousAppsRef.current;

  if (uniqueApps.length === 0) return null;

  return (
    <div>
      <SummaryApps uniqueApps={uniqueApps} darkMode={true} />
    </div>
  );
}

function SummaryAppsWrapper() {
  return (
    <Suspense fallback={null}>
      <SuspendedSummaryAppsWrapper />
    </Suspense>
  );
}

export default function MultiSelectPanel() {
  const selectedNodes = useAtomValue(selectedNodesAtom);
  const deselectAllNodes = useSetAtom(deselectAllNodesSetter);
  const deselectAllEdges = useSetAtom(deselectAllEdgesSetter);
  const actorsOfSelectedNodes = useAtomValue(actorsAtomFamily(true));
  const setGraphDirection = useSetAtom(graphDirectionAtom);

  const setAutoLayoutForSelectedNodes = useSetAtom(
    autoLayoutSelectedNodesSetter,
  );
  const createNotification = useSetAtom(createNotificationSetter);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const hasExpandedSelectedZapNodes = useAtomValue(
    hasExpandedSelectedZapNodesAtom,
  );
  const { emitEventOnAutoLayout } = useEmitUserInteractionEvents();

  const runAutoLayoutAndSave = useCallback(async () => {
    const { nodes, isSuccess } = setAutoLayoutForSelectedNodes();
    emitEventOnAutoLayout({ forAllSteps: false });

    if (isSuccess) {
      saveToBackend({ nodes });

      createNotification({
        type: "success",
        message: (
          <AutoLayoutSuccessMessage
            isMultiSelect={true}
            hasExpandedSelectedZapNodes={hasExpandedSelectedZapNodes}
          />
        ),
      });
    }
  }, [
    setAutoLayoutForSelectedNodes,
    createNotification,
    saveToBackend,
    hasExpandedSelectedZapNodes,
    emitEventOnAutoLayout,
  ]);

  const onClose = () => {
    deselectAllNodes();
    deselectAllEdges();
  };

  if (selectedNodes.length > 1) {
    return (
      <Panel position="top-right">
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className={styles.multiSelectPanel}
        >
          <div className={styles.multiSelectPanelHeader}>
            <Text type="ParagraphHeader3" color="PrimeWhite">
              {selectedNodes.length} steps selected
            </Text>
            <CloseButton onClose={onClose} color="GrayWarm4" size={28} />
          </div>
          {actorsOfSelectedNodes.length > 0 && (
            <div>
              <SummaryActors onlyForSelectedNodes={true} darkMode={true} />
            </div>
          )}
          <SummaryAppsWrapper />
          <div className={styles.multiSelectPanelCTA}>
            <Button
              variant="tertiary"
              onPress={() => {
                setGraphDirection("LR");
                runAutoLayoutAndSave();
                onClose();
              }}
            >
              <div className={styles.flexWrapper}>
                <AutoLayoutIcon width={24} height={24} />
                <span>Auto layout for selected steps (left to right)</span>
              </div>
            </Button>
            <Button
              variant="tertiary"
              onPress={() => {
                setGraphDirection("TB");
                runAutoLayoutAndSave();
                onClose();
              }}
            >
              <div className={styles.flexWrapper}>
                <AutoLayoutIcon width={24} height={24} />
                <span>Auto layout for selected steps (top to bottom)</span>
              </div>
            </Button>
          </div>
        </div>
      </Panel>
    );
  }

  return null;
}
